import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withStyles, Accordion, AccordionDetails, AccordionSummary, Grid, Box } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isNumber from 'lodash/isNumber';
import classNames from 'classnames';
import ceil from 'lodash/ceil';
import { ADGMPanel, ADGMPagination, ADGMGridLayout } from 'aurora-components';

import styles from './styles';

const DEFAULT_PAGE = 1;
const DEFAULT_ITEMS_PER_PAGE = 5;

const PropertyListAccordion = ({
  classes,
  data,
  itemsPerPage,
  customClasses,
  headerDataByRow,
  bodyDataByRow,
  imageToRender,
  ...panelProps
}) => {
  if (isEmpty(data)) return null;
  const [expanded, setExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);

  const perPage = useMemo(() => (isNumber(itemsPerPage) ? itemsPerPage : DEFAULT_ITEMS_PER_PAGE), [itemsPerPage]);
  const endIndex = useMemo(() => currentPage * perPage, [currentPage, perPage]);
  const startIndex = useMemo(() => endIndex - perPage, [endIndex, perPage]);

  const handleChange = panel => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const renderGridLayout = (gridData, columns, mobColumns, otherProps = {}) => (
    <ADGMGridLayout data={gridData} sm={columns ?? 4} md={columns ?? 4} xs={mobColumns ?? 6} {...otherProps} />
  );

  const renderPropertyAccordion = (property, index) => {
    if (!isObject(property) || isEmpty(property)) return null;
    const titleData = Object.keys(property)
      ?.slice(0, headerDataByRow)
      ?.reduce((curData, key) => {
        return { ...curData, [key]: property[key] };
      }, {});
    const collapsedData = Object.keys(property)
      ?.slice(headerDataByRow)
      ?.reduce((curData, key) => {
        return { ...curData, [key]: property[key] };
      }, {});
    return (
      <Accordion
        className={classNames(classes.accordion, customClasses?.accordion)}
        expanded={expanded === `panel${index}`}
        onChange={handleChange(`panel${index}`)}
      >
        <AccordionSummary
          className={classNames(
            classes.accordionSummary,
            { [classes.accordingSummaryImage]: imageToRender },
            customClasses?.accordionSummary,
          )}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${index}bh-content`}
          id={`panel${index}bh-header`}
        >
          {imageToRender && <Box className={customClasses?.selectedPlotsIcon}>{imageToRender}</Box>}
          <Grid xs={12} md={12}>
            {renderGridLayout(titleData, 12 / headerDataByRow)}
          </Grid>
        </AccordionSummary>
        <AccordionDetails
          className={classNames(
            classes.accordionDetails,
            { [classes.accordingSummaryImage]: imageToRender },
            customClasses?.accordionDetails,
          )}
        >
          {renderGridLayout(collapsedData, 12 / bodyDataByRow)}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <ADGMPanel {...panelProps}>
      <div className={classNames(classes.dataContainer, customClasses?.listContainer)}>
        {isArray(data) && data?.slice(startIndex, endIndex)?.map((property, i) => renderPropertyAccordion(property, i))}
        {!isArray(data) && isObject(data) && renderGridLayout(data, null, null, { hasPadding: true })}
      </div>
      {isArray(data) && (
        <div className={classNames(classes.paginationContainer, customClasses.paginationContainer)}>
          <ADGMPagination
            totalPages={ceil(data?.length / itemsPerPage)}
            currentPage={Number(currentPage)}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </ADGMPanel>
  );
};

PropertyListAccordion.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  itemsPerPage: PropTypes.number,
  headerDataByRow: PropTypes.number,
  bodyDataByRow: PropTypes.number,
  imageToRender: PropTypes.node,
  customClasses: PropTypes.object,
};

PropertyListAccordion.defaultProps = {
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  headerDataByRow: 3,
  bodyDataByRow: 3,
  imageToRender: null,
  customClasses: {},
};

export default withStyles(styles)(PropertyListAccordion);
